/**
 * Billing HTTP client — thin wrappers around the backend's
 * GET /api/billing/daily and GET /api/billing/episodes endpoints.
 *
 * Plan: docs/plans/agent-billing-phase-bc.md — Stage 7.
 *
 * Returned row shapes mirror the backend response verbatim so the UI can
 * render tables without re-mapping keys.
 */

import { authHeaders } from "../store/authStore";

export interface DailyUsageTokens {
  readonly input: number;
  readonly output: number;
  readonly cache_read: number;
  readonly cache_write: number;
  readonly cache_write_1h: number;
  readonly reasoning: number;
}

export interface DailyUsageRow {
  readonly day: string;
  readonly provider: string;
  readonly model: string;
  readonly tokens: DailyUsageTokens;
  readonly cost_micros: number;
  readonly callCount: number;
}

export interface RecentEpisodeRow {
  readonly episode_id: string;
  readonly started_at: string;
  readonly endedAt: string;
  readonly totalTokens: number;
  readonly cost_micros: number;
  readonly callCount: number;
}

export interface DailyUsageParams {
  readonly from?: string;
  readonly to?: string;
}

export interface RecentEpisodesParams {
  readonly limit?: number;
  readonly offset?: number;
}

function buildUrl(
  baseUrl: string,
  path: string,
  params: Record<string, string | number | undefined>,
): URL {
  const url = new URL(path, baseUrl);
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    url.searchParams.set(key, typeof value === "number" ? value.toString() : value);
  }
  return url;
}

async function getJson<T>(url: URL): Promise<T> {
  const response = await fetch(url.toString(), {
    method: "GET",
    headers: authHeaders({ Accept: "application/json" }),
  });
  if (!response.ok) {
    throw new Error(`HTTP ${String(response.status)} ${response.statusText}`);
  }
  return (await response.json()) as T;
}

export async function fetchDailyUsage(
  baseUrl: string,
  params: DailyUsageParams,
): Promise<readonly DailyUsageRow[]> {
  const url = buildUrl(baseUrl, "/api/billing/daily", {
    from: params.from,
    to: params.to,
  });
  const { rows } = await getJson<{ rows: readonly DailyUsageRow[] }>(url);
  return rows;
}

export async function fetchRecentEpisodes(
  baseUrl: string,
  params: RecentEpisodesParams,
): Promise<readonly RecentEpisodeRow[]> {
  const url = buildUrl(baseUrl, "/api/billing/episodes", {
    limit: params.limit,
    offset: params.offset,
  });
  const { rows } = await getJson<{ rows: readonly RecentEpisodeRow[] }>(url);
  return rows;
}
